import {
  EmptyState,
  FlexBox,
  Page,
  Spinner,
  Table,
} from '@dixa/lib-shared-habla';
import { useMemo, useState } from 'react';

import { LocalNotificationParams, notificationsApi } from 'apis/notifications';
import { useAppSelector } from 'utils/redux/useAppSelector';

import {
  BulkActionNotification,
  NotificationRow,
  UserMentionedNotification,
} from './components';
import { AcwNotification } from './components/acwNotification/acwNotification';
import { HistoricalNotification } from './notificationsPage.types';
import { transformAndSortNotifications } from './utils/transformNotification';

export const NotificationsPage = (): JSX.Element => {
  const [currentNotification, setCurrentNotification] = useState<string | null>(
    null,
  );
  const allUsers = useAppSelector((state) => state.presence.allPresence);

  const { persistedNotifications, isLoading } =
    notificationsApi.useReadNotificationsQuery(
      {
        unreadOnly: false,
      },
      {
        refetchOnMountOrArgChange: false,
        selectFromResult: ({ data, ...rest }) => ({
          persistedNotifications: data?.data.notifications ?? [],
          ...rest,
        }),
      },
    );

  const notifications = useMemo(() => {
    const reversed = true;

    return transformAndSortNotifications(
      persistedNotifications,
      allUsers,
      reversed,
    );
  }, [allUsers, persistedNotifications]);

  const renderLocalNotification = (
    notification: HistoricalNotification,
    { type }: LocalNotificationParams,
  ) => {
    switch (type) {
      case 'acw':
        return (
          <AcwNotification key={notification.id} notification={notification} />
        );
      default:
        return (
          <NotificationRow key={notification.id} notification={notification} />
        );
    }
  };

  const renderNotification = (notification: HistoricalNotification) => {
    switch (notification.payload?.type) {
      case 'bulkaction:started':
      case 'bulkaction:finished':
        return (
          <BulkActionNotification
            key={notification.id}
            currentNotificationId={currentNotification}
            notification={notification}
            bulkActionData={notification.payload.params}
            setCurrentNotification={setCurrentNotification}
          />
        );
      case 'mention:user_mentioned':
        return (
          <UserMentionedNotification
            key={notification.id}
            notification={notification}
            userMentionedData={notification.payload.params}
          />
        );
      case 'local_notification:displayed':
        return renderLocalNotification(
          notification,
          notification.payload.params,
        );
      default:
        return (
          <NotificationRow key={notification.id} notification={notification} />
        );
    }
  };

  return (
    <Page>
      <Page.Head title="Notifications" />

      <Page.Body>
        <Page.Section>
          {notifications.length === 0 &&
            (isLoading ? (
              <FlexBox justifyContent="center">
                <Spinner size={32} />
              </FlexBox>
            ) : (
              <FlexBox
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                flex="1"
                height="100%"
              >
                <EmptyState
                  title="No notifications"
                  description="You are all caught up."
                  icon="bell"
                />
              </FlexBox>
            ))}
          {notifications.length > 0 && (
            <Table>
              <Table.Tbody>{notifications.map(renderNotification)}</Table.Tbody>
            </Table>
          )}
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
